import { endOfMonth, parseISO, startOfMonth, subMonths } from 'date-fns'
import {
  InsightsHeadcountFragment,
  InsightsJoinerFragment,
  InsightsLeaverFragment,
} from 'generated/resources/graphql'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import formatDecimals from 'modules/insights/lib/formatDecimals'

import { ExportRow, GenerateExport } from './export'

const parseDate = (date: Date) => dateToString(date, 'backend')

const findHeadcount = <
  T extends InsightsHeadcountFragment | InsightsLeaverFragment | InsightsJoinerFragment,
>(
  collection: T[],
  date: Date
): T | undefined => collection.find(({ endDate }) => endDate === parseDate(date))

const headcountMonths = (sub: 1 | 2 = 1, date: Date) => {
  const currentMonth = startOfMonth(date)
  const lastMonth = endOfMonth(subMonths(currentMonth, sub))
  const monthPreviousLastMonth = endOfMonth(subMonths(lastMonth, 1))

  return [lastMonth, monthPreviousLastMonth] as const
}

const findHeadcounts = (
  headcount: InsightsHeadcountFragment[],
  leavers: InsightsLeaverFragment[],
  sub: 1 | 2 = 1,
  date: Date
) => {
  const [leaversMonth, headcountMonth] = headcountMonths(sub, date)

  const headcountResult = findHeadcount(headcount, headcountMonth)?.headcount || 0
  const leaversResult = findHeadcount(leavers, leaversMonth)?.leavers || 0

  return [headcountResult, leaversResult] as const
}

const calculateRate = (turnoverRateData: {
  headcount: number
  joiners: number
  leavers: number
}) => {
  const { headcount, joiners, leavers } = turnoverRateData
  const avgNumberOfEmployees = (headcount * 2 + joiners - leavers) / 2

  if (avgNumberOfEmployees === 0) return 0

  return (leavers / avgNumberOfEmployees) * 100
}

const calculatePreviousCurrentTurnover = (
  headcount: InsightsHeadcountFragment[],
  leavers: InsightsLeaverFragment[],
  joiners: InsightsJoinerFragment[],
  date: Date
) => {
  const [joinersMonth] = headcountMonths(1, date)

  const [currentHeadcount, currentLeavers] = findHeadcounts(headcount, leavers, 1, date)
  const currentJoinerHeadcount = findHeadcount(joiners, joinersMonth)

  const currentRate = calculateRate({
    headcount: currentHeadcount,
    joiners: currentJoinerHeadcount?.joiners || 0,
    leavers: currentLeavers,
  })

  const [lastJoinersMonth] = headcountMonths(2, date)
  const [lastMonthHeadcount, lastMonthLeavers] = findHeadcounts(headcount, leavers, 2, date)
  const lastMonthJoinersHeadcount = findHeadcount(joiners, lastJoinersMonth)

  const previousRate = calculateRate({
    headcount: lastMonthHeadcount,
    joiners: lastMonthJoinersHeadcount?.joiners || 0,
    leavers: lastMonthLeavers,
  })

  const diff = formatDecimals(currentRate) - formatDecimals(previousRate)

  return [currentRate, formatDecimals(diff)] as const
}

export type TurnoverByRanges = {
  value: number
  startDate: string
  endDate: string
}

export default class Turnover implements GenerateExport {
  private headcountEvolutionByMonth: InsightsHeadcountFragment[]
  private leavers: InsightsLeaverFragment[]
  private joiners: InsightsJoinerFragment[]

  constructor(
    headcountEvolutionByMonth: InsightsHeadcountFragment[],
    leavers: InsightsLeaverFragment[],
    joiners: InsightsJoinerFragment[]
  ) {
    this.headcountEvolutionByMonth = headcountEvolutionByMonth
    this.leavers = leavers
    this.joiners = joiners
  }

  isEmpty() {
    const headcount = this.headcountEvolutionByMonth.every((record) => record.headcount == 0)
    const leavers = this.leavers.every((record) => record.leavers == 0)
    const joiners = this.joiners.every((record) => record.joiners == 0)

    return [headcount, leavers, joiners].every((isEmpty) => isEmpty)
  }

  export() {
    const result = this.generate()

    const data: ExportRow[][] = [
      [
        i18n.t('insights.widgets.employee_turnover_rate.title'),
        i18n.t('insights.widgets.shared.from_previous_months'),
      ],
      [formatDecimals(result.value), result.formatted],
    ]

    return {
      title: i18n.t('insights.widgets.employee_turnover_rate.title'),
      data,
    }
  }

  generate() {
    const [currentTurnoverRate, diff] = calculatePreviousCurrentTurnover(
      this.headcountEvolutionByMonth,
      this.leavers,
      this.joiners,
      // This line of code substracts one month so as to calculate the data of the previous month,
      // due to the fact that retention rate only crunch the data of the previous month,
      subMonths(new Date(), 1)
    )
    const formattedDiff = formatDecimals(Math.abs(diff))

    return {
      value: formatDecimals(currentTurnoverRate),
      diff: diff,
      formatted: formattedDiff,
    }
  }

  generateByRanges(): TurnoverByRanges[] {
    return this.headcountEvolutionByMonth
      .slice(3, this.headcountEvolutionByMonth.length - 1)
      .map(({ startDate, endDate }) => {
        const [currentTurnoverRate, _] = calculatePreviousCurrentTurnover(
          this.headcountEvolutionByMonth,
          this.leavers,
          this.joiners,
          parseISO(startDate)
        )
        const value = formatDecimals(currentTurnoverRate)
        return { value, startDate, endDate }
      })
  }
}
